<script setup>
import { Head } from '@inertiajs/vue3';
import { ref } from 'vue';
import Button from '@/Components/Button.vue';
import CreateFormModal from '@/Components/CreateFormModal.vue';
import moment from 'moment';
import { router } from '@inertiajs/vue3';

// Define props to receive data from the backend
const props = defineProps({
    stats: {
        type: Object,
        default: () => ({
            unsigned_entree: 0,
            completed_forms: 0,
            draft_forms: 0,
            total_forms: 0
        })
    },
    recentForms: {
        type: Array,
        default: () => []
    },
    users: {
        type: Array,
        default: () => []
    },
    currentUserId: {
        type: Number,
        default: null
    },
    sentForms: {
        type: Array,
        default: () => []
    }
});

// Modal state
const showCreateFormModal = ref(false);

// Dashboard stats based on real data needs
const dashboardStats = [
    { 
        title: 'Niet-ondertekende entree formulieren', 
        value: props.stats.sent_forms || props.stats.unsigned_entree, 
        icon: 'exclamation-triangle', 
        color: 'bg-red-500',
        description: 'Vereist aandacht'
    },
    { 
        title: 'Voltooide formulieren', 
        value: props.stats.completed_forms, 
        icon: 'check-circle', 
        color: 'bg-ag',
        description: 'Afgerond'
    },
    { 
        title: 'Open concepten', 
        value: props.stats.draft_forms, 
        icon: 'clock', 
        color: 'bg-amber-500',
        description: 'In bewerking'
    }
];

// Helper functions
function formatAddress(form) {
    if (!form.address) return 'Geen adres';
    
    return (form.address.route || '') + ' ' + 
           (form.address.street_number || '') +
           (form.address_addition ? ' ' + form.address_addition : '') + 
           (form.room_number ? '-' + form.room_number : '');
}

function translateStatus(status) {
    switch (status) {
        case 'pre':
            return 'Vooroplevering';
        case 'end':
            return 'Eindoplevering';
        case 'completed':
            return 'Eindoplevering afgerond';
        case 'sent':
            return 'Entree verstuurd';
        case 'signed':
            return 'Entree ondertekend';
        default:
            return 'Onbekend';
    }
}

function getStatusColor(status) {
    switch (status) {
        case 'pre':
            return 'bg-kr/10 text-kr border-kr/60';
        case 'end':
        case 'completed':
            return 'bg-og/10 text-og border-og/60';
        case 'sent':
            return 'bg-hb/10 text-hb border-hb/60';
        case 'signed':
            return 'bg-wg/10 text-wg border-wg/60';
        default:
            return 'bg-gray-100 text-gray-800 border-gray-300';
    }
}

function goToForm(formId) {
    router.visit(`/formulieren/${formId}/edit`, {
        preserveState: true,
        preserveScroll: true
    });
}

// Modal handlers
function openCreateFormModal() {
    showCreateFormModal.value = true;
}

function closeCreateFormModal() {
    showCreateFormModal.value = false;
}

function handleFormCreated() {
    // Modal will emit 'success' and 'close' events
    // The form creation will redirect to the edit page
    closeCreateFormModal();
}
</script>

<template>
    <Head title="Dashboard" />

    <!-- Dashboard content -->
    <div class="p-4">
        <!-- Stats section -->
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-6">
            <div v-for="(stat, index) in dashboardStats" :key="index" class="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
                <div class="flex items-center p-4">
                    <div :class="[stat.color, 'flex-shrink-0 rounded-md p-3']">
                        <font-awesome-icon :icon="['fas', stat.icon]" class="h-6 w-6 text-white" />
                    </div>
                    <div class="ml-4 flex-1">
                        <dl>
                            <dt class="text-sm font-medium text-gray-500 truncate">{{ stat.title }}</dt>
                            <dd class="text-2xl font-bold text-gray-900">{{ stat.value }}</dd>
                            <dd class="text-xs text-gray-400">{{ stat.description }}</dd>
                        </dl>
                    </div>
                </div>
            </div>
        </div>

        <!-- Unsigned entree forms section -->
        <div class="bg-white shadow rounded-lg overflow-hidden mb-6">
            <div class="p-4 sm:p-6 flex justify-between items-center border-b border-gray-200">
            <h3 class="text-lg leading-6 font-medium text-gray-900">Niet-ondertekende entree formulieren</h3>
            <Button type="href" href="/formulieren" class="bg-hbz hover:bg-hb px-4 py-2 rounded-md transition-colors">
                <span class="text-white text-sm font-medium">Alle formulieren</span>
            </Button>
            </div>
            <div v-if="sentForms.length > 0">
            <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Formulier</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adres</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Laatst bewerkt</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="form in sentForms" :key="form.id" 
                    class="hover:bg-gray-50 transition-colors cursor-pointer"
                    @click="goToForm(form.id)">
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-hbz">
                        <span class="hover:underline">
                        {{ form.sjht_rel_id || `OPL-${form.id}` }}
                        </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 truncate max-w-lg">
                        {{ formatAddress(form) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span class="capitalize">{{ form.type === 'self' ? 'Zelfstandig' : form.type === 'group' ? 'Onzelfstandig' : (form.type || 'Onbekend') }}</span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {{ form.updated_at ? moment(form.updated_at).format('DD-MM-YYYY HH:mm') : '-' }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                        <div class="flex items-center gap-2">
                        <span :class="[
                            getStatusColor(form.status),
                            'px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border'
                        ]">
                            {{ translateStatus(form.status) }}
                        </span>
                        <div v-if="form.concept" 
                            class="border-gray-400 border bg-gray-200/10 text-gray-400 rounded-full h-6 px-2 py-1 flex items-center justify-center">
                            <font-awesome-icon :icon="['fas', 'pen-ruler']" class="h-3 w-3" />
                        </div>
                        </div>
                    </td>
                    </tr>
                </tbody>
                </table>
            </div>
            </div>
            <div v-else class="p-8 text-center">
            <font-awesome-icon :icon="['fas', 'inbox']" class="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p class="text-gray-500">Geen niet-ondertekende entree formulieren gevonden.</p>
            </div>
        </div>

        <!-- Recent forms section -->
        <div class="bg-white shadow rounded-lg overflow-hidden mb-6">
            <div class="p-4 sm:p-6 flex justify-between items-center border-b border-gray-200">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Laatst bewerkte formulieren</h3>
                <Button type="href" href="/formulieren" class="bg-hbz hover:bg-hb px-4 py-2 rounded-md transition-colors">
                    <span class="text-white text-sm font-medium">Alle formulieren</span>
                </Button>
            </div>
            <div v-if="recentForms.length > 0">
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Formulier</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adres</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Laatst bewerkt</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-for="form in recentForms" :key="form.id" 
                                class="hover:bg-gray-50 transition-colors cursor-pointer"
                                @click="goToForm(form.id)">
                                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-hbz">
                                    <span class="hover:underline">
                                        {{ form.sjht_rel_id || `OPL-${form.id}` }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 truncate max-w-lg">
                                    {{ formatAddress(form) }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <span class="capitalize">{{ form.type === 'self' ? 'Zelfstandig' : form.type === 'group' ? 'Onzelfstandig' : (form.type || 'Onbekend') }}</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {{ form.updated_at ? moment(form.updated_at).format('DD-MM-YYYY HH:mm') : '-' }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center gap-2">
                                        <span :class="[
                                            getStatusColor(form.status),
                                            'px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border'
                                        ]">
                                            {{ translateStatus(form.status) }}
                                        </span>
                                        <div v-if="form.concept" 
                                            class="border-gray-400 border bg-gray-200/10 text-gray-400 rounded-full h-6 px-2 py-1 flex items-center justify-center">
                                            <font-awesome-icon :icon="['fas', 'pen-ruler']" class="h-3 w-3" />
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div v-else class="p-8 text-center">
                <font-awesome-icon :icon="['fas', 'inbox']" class="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p class="text-gray-500">Nog geen formulieren aangemaakt.</p>
            </div>
        </div>

        <!-- Quick actions section -->
        <div class="bg-white shadow rounded-lg mb-6">
            <div class="p-4 sm:p-6 border-b border-gray-200">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Snelle acties</h3>
                <p class="mt-1 text-sm text-gray-500">Begin met het aanmaken van een nieuw formulier</p>
            </div>
            <div class="p-4 sm:p-6">
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <Button @click="openCreateFormModal" class="flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-hbz hover:bg-hb transition-colors">
                        <font-awesome-icon :icon="['fas', 'file-plus']" class="mr-2 h-5 w-5" />
                        Nieuw formulier aanmaken
                    </Button>
                    
                    <!-- Future: Add more quick actions here as needed -->
                    <div class="flex items-center justify-center px-6 py-3 border-2 border-dashed border-gray-300 rounded-md text-gray-400">
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2 h-5 w-5" />
                        <span class="text-base font-medium">Meer acties binnenkort</span>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Create Form Modal -->
    <CreateFormModal 
        :open="showCreateFormModal" 
        :users="users" 
        :currentUserId="currentUserId"
        @close="closeCreateFormModal"
        @success="handleFormCreated"
    />
</template>